// ---------------------------------------------------------------------------
// Test Mode — simulated team (18 people) for Clean Code Culture SRL (CLE-250).
//
// People are the backbone of the coherent dataset: their stable ids are reused
// as task assignees, project members, allocations and org-chart nodes. Each
// person carries a capacity (h/week) and a utilization that the projects module
// keeps consistent with their allocations.
// ---------------------------------------------------------------------------

import type { Person, OrgChart, OrgChartNode, PersonResource } from '../lib/peopleApi';
import { isoDateTime } from './company';
import { departmentIdForName } from './departments';

interface PersonSeed {
  id: string;
  fullName: string;
  email: string;
  title: string;
  department: string;
  managerId: string | null;
  utilization: number; // percent
}

// 1 CEO, 1 CTO, 2 PMs, 1 QA Lead, 1 Designer, 8 Devs, 1 DevOps, 1 Marketing,
// 1 Office Manager, 1 Accountant (part-time).
const SEEDS: PersonSeed[] = [
  {
    id: 'p1',
    fullName: 'Andrei Pata',
    email: 'andrei.pata@cleancodeculture.ro',
    title: 'CEO / Founder',
    department: 'Management',
    managerId: null,
    utilization: 70,
  },
  {
    id: 'p2',
    fullName: 'Ioana Pop',
    email: 'ioana.pop@cleancodeculture.ro',
    title: 'CTO',
    department: 'Engineering',
    managerId: 'p1',
    utilization: 85,
  },
  {
    id: 'p3',
    fullName: 'Mihai Georgescu',
    email: 'mihai.georgescu@cleancodeculture.ro',
    title: 'Project Manager',
    department: 'Delivery',
    managerId: 'p2',
    utilization: 95,
  },
  {
    id: 'p4',
    fullName: 'Elena Dumitrescu',
    email: 'elena.dumitrescu@cleancodeculture.ro',
    title: 'Project Manager',
    department: 'Delivery',
    managerId: 'p2',
    utilization: 90,
  },
  {
    id: 'p5',
    fullName: 'Radu Constantin',
    email: 'radu.constantin@cleancodeculture.ro',
    title: 'QA Lead',
    department: 'Quality',
    managerId: 'p2',
    utilization: 80,
  },
  {
    id: 'p6',
    fullName: 'Cristina Stan',
    email: 'cristina.stan@cleancodeculture.ro',
    title: 'UI/UX Designer',
    department: 'Design',
    managerId: 'p2',
    utilization: 75,
  },
  {
    id: 'p7',
    fullName: 'Alexandru Ionescu',
    email: 'alexandru.ionescu@cleancodeculture.ro',
    title: 'Senior Developer',
    department: 'Engineering',
    managerId: 'p3',
    utilization: 100,
  },
  {
    id: 'p8',
    fullName: 'Bogdan Marin',
    email: 'bogdan.marin@cleancodeculture.ro',
    title: 'Senior Developer',
    department: 'Engineering',
    managerId: 'p3',
    utilization: 100,
  },
  {
    id: 'p9',
    fullName: 'Diana Vasile',
    email: 'diana.vasile@cleancodeculture.ro',
    title: 'Mid Developer',
    department: 'Engineering',
    managerId: 'p3',
    utilization: 95,
  },
  {
    id: 'p10',
    fullName: 'Vlad Popescu',
    email: 'vlad.popescu@cleancodeculture.ro',
    title: 'Mid Developer',
    department: 'Engineering',
    managerId: 'p4',
    utilization: 90,
  },
  {
    id: 'p11',
    fullName: 'Andreea Toma',
    email: 'andreea.toma@cleancodeculture.ro',
    title: 'Mid Developer',
    department: 'Engineering',
    managerId: 'p4',
    utilization: 85,
  },
  {
    id: 'p12',
    fullName: 'Sorin Lazar',
    email: 'sorin.lazar@cleancodeculture.ro',
    title: 'Junior Developer',
    department: 'Engineering',
    managerId: 'p7',
    utilization: 70,
  },
  {
    id: 'p13',
    fullName: 'Gabriela Neagu',
    email: 'gabriela.neagu@cleancodeculture.ro',
    title: 'Junior Developer',
    department: 'Engineering',
    managerId: 'p8',
    utilization: 65,
  },
  {
    id: 'p14',
    fullName: 'Cătălin Barbu',
    email: 'catalin.barbu@cleancodeculture.ro',
    title: 'Senior Developer',
    department: 'Engineering',
    managerId: 'p4',
    utilization: 100,
  },
  {
    id: 'p15',
    fullName: 'Raluca Florea',
    email: 'raluca.florea@cleancodeculture.ro',
    title: 'DevOps Engineer',
    department: 'Infrastructure',
    managerId: 'p2',
    utilization: 80,
  },
  {
    id: 'p16',
    fullName: 'Tudor Albu',
    email: 'tudor.albu@cleancodeculture.ro',
    title: 'Marketing Manager',
    department: 'Marketing',
    managerId: 'p1',
    utilization: 60,
  },
  {
    id: 'p17',
    fullName: 'Monica Sandu',
    email: 'monica.sandu@cleancodeculture.ro',
    title: 'Office Manager',
    department: 'Operations',
    managerId: 'p1',
    utilization: 60,
  },
  {
    id: 'p18',
    fullName: 'Liviu Crețu',
    email: 'liviu.cretu@cleancodeculture.ro',
    title: 'Accountant (part-time)',
    department: 'Finance',
    managerId: 'p1',
    utilization: 50,
  },
];

const CREATED_AT = isoDateTime(-540, 9); // ~18 months ago
const UPDATED_AT = isoDateTime(-2, 14);

// Lightweight reference used across other entities (assignee / member shapes).
export interface PersonRef {
  id: string;
  name: string;
}

export const PEOPLE_REFS: PersonRef[] = SEEDS.map((s) => ({ id: s.id, name: s.fullName }));

export function personRef(id: string): PersonRef | null {
  return PEOPLE_REFS.find((p) => p.id === id) ?? null;
}

export function buildPeople(): Person[] {
  return SEEDS.map((s) => {
    const capacity = 40;
    const allocatedHours = Math.round((capacity * s.utilization) / 100);
    return {
      id: s.id,
      userId: s.id,
      fullName: s.fullName,
      email: s.email,
      phone: `+40 7${s.id.replace('p', '').padStart(2, '0')} 123 456`,
      title: s.title,
      department: s.department,
      departmentId: departmentIdForName(s.department),
      location: 'Cluj-Napoca',
      avatarUrl: null,
      capacity,
      startDate: null,
      managerId: s.managerId,
      utilizationPercent: s.utilization,
      allocatedHours,
      isOverallocated: s.utilization > 100,
      isActive: true,
      allocations: [], // populated by projects module via getPeople()
      createdAt: CREATED_AT,
      updatedAt: UPDATED_AT,
    };
  });
}

// --- Person activity (demo audit) ------------------------------------------
// Must match PersonActivityItem / PersonActivityResult from lib/peopleApi.ts.

import type {
  PersonActivityItem,
  PersonActivityResult,
  PersonActivityTaskRef,
} from '../lib/peopleApi';

export function buildPersonActivity(
  personId: string,
  /** The person's real demo tasks; the feed points at these when there are any. */
  tasks: PersonActivityTaskRef[] = [],
): PersonActivityResult {
  const person = PEOPLE_REFS.find((p) => p.id === personId);
  const name = person?.name ?? 'Necunoscut';

  // Real tasks first, so clicking a row opens the task instead of landing on
  // "task not found"; synthetic references only for someone with no tasks.
  const taskRef = (n: number): PersonActivityTaskRef =>
    tasks.length > 0 ? tasks[n % tasks.length]! : syntheticRef(n);
  const syntheticRef = (n: number): PersonActivityTaskRef => ({
    id: `t-${personId}-${n}`,
    identifier: `CLE-${100 + Number(personId.replace(/\D/g, '')) * 10 + n}`,
    title: [
      `Implementare funcționalitate`,
      `Refactorizare modul`,
      `Fix bug critic`,
      `Actualizare teste`,
      `Review cod`,
      `Optimizare performanță`,
      `Documentare API`,
      `Configurare CI/CD`,
    ][n % 8],
    projectId: `pr${(n % 3) + 1}`,
    projectName: ['E-commerce Platform', 'Dashboard Analytics', 'App Mobil'][n % 3],
    projectCode: ['ECOM', 'DASH', 'MOB'][n % 3],
  });

  const items: PersonActivityItem[] = [
    {
      id: `act-${personId}-1`,
      field: 'status',
      oldValue: 'in_progress',
      newValue: 'done',
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-1, 14),
      user: { id: personId, name },
      task: taskRef(0),
    },
    {
      id: `act-${personId}-2`,
      field: 'created',
      oldValue: null,
      newValue: null,
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-2, 10),
      user: { id: personId, name },
      task: taskRef(1),
    },
    {
      id: `act-${personId}-3`,
      field: 'comment_added',
      oldValue: null,
      newValue: null,
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-2, 16),
      user: { id: personId, name },
      task: taskRef(0),
    },
    {
      id: `act-${personId}-4`,
      field: 'assigneeId',
      oldValue: null,
      newValue: personId,
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-3, 9),
      user: { id: 'p1', name: 'Mihai Georgescu' },
      task: taskRef(2),
    },
    {
      id: `act-${personId}-5`,
      field: 'status',
      oldValue: 'todo',
      newValue: 'in_progress',
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-4, 11),
      user: { id: personId, name },
      task: taskRef(2),
    },
    {
      id: `act-${personId}-6`,
      field: 'status',
      oldValue: 'in_progress',
      newValue: 'done',
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-5, 15),
      user: { id: personId, name },
      task: taskRef(3),
    },
    {
      id: `act-${personId}-7`,
      field: 'priority',
      oldValue: 'MEDIUM',
      newValue: 'HIGH',
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-6, 10),
      user: { id: personId, name },
      task: taskRef(4),
    },
    {
      id: `act-${personId}-8`,
      field: 'estimate',
      oldValue: null,
      newValue: '8',
      oldDisplay: null,
      newDisplay: null,
      createdAt: isoDateTime(-7, 14),
      user: { id: personId, name },
      task: taskRef(5),
    },
  ];
  return { source: 'local', items };
}

export function buildOrgChart(): OrgChart {
  const people = buildPeople();
  const byManager = new Map<string | null, Person[]>();
  for (const p of people) {
    const list = byManager.get(p.managerId) ?? [];
    list.push(p);
    byManager.set(p.managerId, list);
  }
  const toNode = (p: Person): OrgChartNode => ({
    id: p.id,
    fullName: p.fullName,
    email: p.email,
    title: p.title,
    department: p.department,
    avatarUrl: p.avatarUrl,
    managerId: p.managerId,
    reports: (byManager.get(p.id) ?? []).map(toNode),
  });
  const roots = (byManager.get(null) ?? []).map(toNode);
  return { roots, unassigned: [], total: people.length };
}

// --- Person resources (flat list of documents) ------------------------------
// Must match PersonResource from lib/peopleApi.ts.

export function buildPersonResources(personId: string): PersonResource[] {
  const seed = SEEDS.find((s) => s.id === personId);
  if (!seed) return [];

  return [
    {
      id: `${personId}-res-1`,
      personId,
      name: `Contract colaborare — ${seed.fullName}.pdf`,
      description: null,
      category: 'Contracte',
      fileType: 'PDF',
      fileSize: 214_500,
      s3Key: `people/${personId}/resources/Contracte/contract.pdf`,
      s3Url: null,
      uploadedAt: isoDateTime(-300, 10),
    },
    {
      id: `${personId}-res-2`,
      personId,
      name: `Factură ${seed.fullName} — luna trecută.pdf`,
      description: null,
      category: 'Facturi',
      fileType: 'PDF',
      fileSize: 88_200,
      s3Key: `people/${personId}/resources/Facturi/factura-1.pdf`,
      s3Url: null,
      uploadedAt: isoDateTime(-30, 11),
    },
    {
      id: `${personId}-res-3`,
      personId,
      name: `Adeverință — ${seed.fullName}.pdf`,
      description: null,
      category: 'Altele',
      fileType: 'PDF',
      fileSize: 45_700,
      s3Key: `people/${personId}/resources/Altele/adeverinta.pdf`,
      s3Url: null,
      uploadedAt: isoDateTime(-12, 9),
    },
  ];
}
